import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import {
  CreditCard, Check, Sparkles, ArrowRight, ExternalLink,
  AlertTriangle, Clock, X,
} from "lucide-react";
import { Button } from "../components/ui/button";

const fmtDateLong = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

const STATUS_TONE = {
  trialing:  { label: "Trial",     tone: "bg-yellow-50 text-amber-800 ring-yellow-300" },
  active:    { label: "Active",    tone: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  past_due:  { label: "Past due",  tone: "bg-rose-50 text-rose-700 ring-rose-200" },
  canceled:  { label: "Canceled",  tone: "bg-stone-100 text-stone-700 ring-stone-200" },
  unpaid:    { label: "Unpaid",    tone: "bg-rose-50 text-rose-700 ring-rose-200" },
  expired:   { label: "Expired",   tone: "bg-stone-100 text-stone-600 ring-stone-200" },
  incomplete:{ label: "Incomplete",tone: "bg-amber-50 text-amber-800 ring-amber-200" },
};

export default function BillingPage() {
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        api.get("/billing/subscription"),
        api.get("/billing/plans"),
      ]);
      setSub(s.data);
      setPlans(p.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load billing info");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Detect return from Stripe Checkout
  useEffect(() => {
    if (params.get("session_id")) {
      toast.success("Subscription updated — welcome to your new plan!");
      navigate("/settings/billing", { replace: true });
      // give webhook a moment to land, then reload
      setTimeout(load, 1500);
    } else if (params.get("canceled")) {
      toast.info("Checkout canceled — your subscription is unchanged.");
      navigate("/settings/billing", { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const subscribe = async (plan_id) => {
    setLoading(true);
    try {
      const res = await api.post("/billing/checkout", {
        plan_id,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setLoading(true);
    try {
      const res = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open billing portal");
      setLoading(false);
    }
  };

  if (!sub) {
    return <div className="p-8 text-stone-500">Loading billing…</div>;
  }

  const status = STATUS_TONE[sub.plan_status] || STATUS_TONE.trialing;
  const currentPlanMeta = sub.plan_meta;
  const isTrialing = sub.plan_status === "trialing";
  const isActive = sub.plan_status === "active";
  const hasCustomer = !!sub.stripe_customer_id;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="billing-page">
      <div>
        <h1 className="font-serif text-3xl md:text-4xl text-[#072032] tracking-tight">Billing & Subscription</h1>
        <p className="text-sm text-stone-600 mt-1">Manage your SalonFlow plan, payment method, and invoices.</p>
      </div>

      {/* Current status card */}
      <div className="bg-white rounded-2xl ring-1 ring-stone-200 p-6" data-testid="billing-status-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs uppercase tracking-wider text-stone-500">Current plan</span>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ring-1 ${status.tone}`} data-testid="billing-status-badge">
                {status.label}
              </span>
            </div>
            <div className="font-serif text-3xl text-[#072032]">
              {currentPlanMeta?.name || "Free trial"}
            </div>
            {currentPlanMeta && (
              <div className="text-sm text-stone-600 mt-1">
                ${currentPlanMeta.price_usd}/{currentPlanMeta.interval}
              </div>
            )}
          </div>
          <div className="text-right">
            {isTrialing && sub.trial_days_remaining !== null && (
              <div data-testid="billing-trial-countdown">
                <div className="font-serif text-3xl text-[#072032]">{sub.trial_days_remaining}</div>
                <div className="text-xs uppercase tracking-wider text-stone-500">days left in trial</div>
                <div className="text-xs text-stone-500 mt-1">Trial ends {fmtDateLong(sub.trial_ends_at)}</div>
              </div>
            )}
            {isActive && sub.current_period_end && (
              <div>
                <div className="text-sm text-stone-600">Renews on</div>
                <div className="font-medium text-[#072032]">{fmtDateLong(sub.current_period_end)}</div>
              </div>
            )}
          </div>
        </div>
        {sub.locked_out && (
          <div className="mt-4 rounded-xl bg-rose-50 ring-1 ring-rose-200 p-3 flex items-start gap-2" data-testid="billing-lockout-banner">
            <AlertTriangle className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
            <div className="text-sm text-rose-900">
              <span className="font-medium">Your salon is in read-only mode.</span>{" "}
              Choose a plan below to restore full access. Your data is safe and waiting for you.
            </div>
          </div>
        )}
        {hasCustomer && (
          <div className="mt-4">
            <Button
              variant="outline"
              onClick={openPortal}
              disabled={loading}
              data-testid="billing-portal-btn"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Manage payment & invoices
            </Button>
          </div>
        )}
      </div>

      {/* Plan grid */}
      <div>
        <h2 className="font-serif text-2xl text-[#072032] mb-1">
          {isActive ? "Change plan" : "Choose a plan"}
        </h2>
        <p className="text-sm text-stone-600 mb-4">
          {isActive
            ? "Switch to a different tier any time. Pro-rated charges apply."
            : "All plans include a 14-day free trial. Cancel any time."}
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          {plans.map(p => {
            const isCurrent = currentPlanMeta?.plan_id === p.plan_id && isActive;
            const isRec = p.recommended;
            return (
              <div
                key={p.plan_id}
                className={`rounded-2xl p-5 ring-1 transition-all ${
                  isRec ? "ring-[#072032] bg-[#FFE01B]/10 shadow-lg" : "ring-stone-200 bg-white"
                }`}
                data-testid={`billing-plan-${p.plan_id}`}
              >
                {isRec && (
                  <div className="mb-2 text-[10px] uppercase tracking-wider px-2 py-0.5 inline-block rounded-full bg-[#072032] text-[#FFE01B]">
                    Most popular
                  </div>
                )}
                <div className="flex items-baseline gap-1">
                  <h3 className="font-serif text-2xl text-[#072032]">{p.name}</h3>
                </div>
                <p className="text-xs text-stone-500 mt-0.5">{p.tagline}</p>
                <div className="mt-3 flex items-baseline">
                  <span className="font-serif text-4xl text-[#072032]">${p.price_usd}</span>
                  <span className="text-sm text-stone-500 ml-1">/{p.interval}</span>
                </div>
                <div className="text-xs text-stone-500 mt-1">
                  {p.max_staff >= 100 ? "Unlimited" : `Up to ${p.max_staff}`} staff
                  {" · "}
                  {p.max_locations >= 100 ? "unlimited" : p.max_locations} location{p.max_locations === 1 ? "" : "s"}
                </div>
                <ul className="mt-4 space-y-1.5 text-sm text-stone-700">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className={`w-full mt-5 ${
                    isCurrent ? "bg-stone-100 text-stone-500 cursor-not-allowed" :
                    isRec ? "bg-[#072032] text-[#FFE01B] hover:bg-[#0d2e44]" :
                    "bg-[#072032] text-[#F9F6ED] hover:bg-[#0d2e44]"
                  }`}
                  disabled={loading || isCurrent}
                  onClick={() => !isCurrent && subscribe(p.plan_id)}
                  data-testid={`billing-subscribe-${p.plan_id}`}
                >
                  {isCurrent ? (
                    "Your current plan"
                  ) : isActive ? (
                    <>Switch to {p.name} <ArrowRight className="h-4 w-4 ml-2" /></>
                  ) : (
                    <>Start {isTrialing ? "free trial" : "subscription"} <ArrowRight className="h-4 w-4 ml-2" /></>
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="text-xs text-stone-500 text-center pt-4 pb-2 border-t border-stone-200">
        Secure checkout powered by Stripe. Cancel any time from the customer portal.
        Questions? <a href="mailto:support@servicepulseos.org" className="text-[#072032] underline">support@servicepulseos.org</a>
      </div>
    </div>
  );
}


/** Compact banner shown across the app while in trial / lockout / past_due. */
export function TrialBanner() {
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("trial_banner_dismissed") === "1");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/billing/subscription")
       .then(r => setSub(r.data))
       .catch(() => {});
  }, []);

  if (!sub || dismissed) return null;
  // Only show if attention needed
  const status = sub.plan_status;
  const days = sub.trial_days_remaining ?? 99;
  const lockedOut = sub.locked_out;
  const urgent = lockedOut || status === "past_due" || (status === "trialing" && days <= 3);
  const showWarn = status === "trialing" && days <= 7 && days > 3;

  if (!urgent && !showWarn) return null;

  const tone = urgent
    ? "bg-rose-50 ring-rose-200 text-rose-900"
    : "bg-yellow-50 ring-yellow-300 text-amber-900";

  let message = "";
  if (lockedOut) message = "Your salon is in read-only mode. Reactivate to keep using SalonFlow.";
  else if (status === "past_due") message = "Last payment failed. Update your payment method to avoid interruption.";
  else if (status === "trialing" && days <= 3) message = `Your trial ends in ${days} day${days === 1 ? "" : "s"}. Subscribe to keep your data.`;
  else if (showWarn) message = `${days} days left in your free trial. Pick a plan any time.`;

  return (
    <div className={`px-4 py-2 ring-1 ${tone} flex items-center justify-between gap-3`} data-testid="trial-banner">
      <div className="flex items-center gap-2 text-sm">
        {urgent ? <AlertTriangle className="h-4 w-4 shrink-0" /> : <Clock className="h-4 w-4 shrink-0" />}
        <span>{message}</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate("/settings/billing")}
          className="px-3 py-1 rounded-md text-xs font-medium bg-[#072032] text-[#F9F6ED] hover:bg-[#0d2e44]"
          data-testid="trial-banner-cta"
        >
          {urgent ? "Reactivate now" : "View plans"}
        </button>
        {!urgent && (
          <button
            onClick={() => { sessionStorage.setItem("trial_banner_dismissed", "1"); setDismissed(true); }}
            className="text-amber-700 hover:text-amber-900"
            data-testid="trial-banner-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
